import { useCachedGet } from "@/lib/cache";
import { Link } from "react-router-dom";
import LiveNowCard from "@/components/app/LiveNowCard";
import Layout from "@/components/app/Layout";
import { stageLabel } from "@/lib/stages";
import { Briefcase, Users, Clock, TrendingUp, ArrowUpRight, Plus, FileDown } from "lucide-react";

const stageColors = {
  invited: "bg-slate-500",
  screening: "bg-indigo-500",
  incomplete: "bg-amber-500",
  completed: "bg-emerald-500",
  hired: "bg-teal-600",
};

export default function DashboardPage() {
  const [summary] = useCachedGet("/analytics/summary");
  const [jobsData] = useCachedGet("/jobs");
  const jobs = jobsData || [];

  return (
    <Layout
      actions={
        <>
          <a href="/recruitsift-user-guide.pdf" target="_blank" rel="noopener noreferrer" data-testid="download-guide" className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] text-slate-200 hover:bg-white/5 transition-colors">
            <FileDown className="w-4 h-4" /> User Guide (PDF)
          </a>
          <Link to="/app/jobs" data-testid="dashboard-create-job" className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors">
            <Plus className="w-4 h-4" /> New Job
          </Link>
        </>
      }
    >
      <div className="mb-6">
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Overview</div>
        <h1 className="font-display text-3xl font-bold">Recruiter Command Center</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { icon: Briefcase, label: "Open Jobs", value: summary?.open_jobs ?? "—", tint: "text-indigo-300", key: "kpi-jobs", to: "/app/jobs?status=open" },
          { icon: Users, label: "Candidates", value: summary?.total_candidates ?? "—", tint: "text-cyan-300", key: "kpi-cands", to: "/app/candidates" },
          { icon: TrendingUp, label: "Pass Rate", value: summary ? `${summary.pass_rate}%` : "—", tint: "text-emerald-300", key: "kpi-pass" },
          { icon: Clock, label: "Hours Saved", value: summary?.hours_saved ?? "—", tint: "text-amber-300", key: "kpi-hours" },
        ].map((k) => {
          const body = (<>
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-lg bg-white/5 border border-[color:var(--bd-sub)] grid place-items-center">
                <k.icon className={`w-4 h-4 ${k.tint}`} />
              </div>
              {k.to && <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 transition-colors" />}
            </div>
            <div className="text-2xl font-display font-bold">{k.value}</div>
            <div className="text-xs text-slate-400 mt-1">{k.label}</div>
          </>);
          return k.to
            ? <Link key={k.key} to={k.to} data-testid={k.key} className="group tp-card p-5 block transition-shadow hover:shadow-md hover:border-indigo-200">{body}</Link>
            : <div key={k.key} data-testid={k.key} className="tp-card p-5">{body}</div>;
        })}
      </div>

      <div className="tp-card p-6 mb-8" data-testid="funnel-card">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-semibold">Pipeline Funnel</div>
          <div className="text-xs text-slate-500">across jobs you can access</div>
        </div>
        <div className="space-y-2.5">
          {summary && Object.entries(summary.funnel).map(([k, v]) => {
            const max = Math.max(...Object.values(summary.funnel), 1);
            const pct = (v / max) * 100;
            return (
              <div key={k} className="flex items-center gap-3">
                <div className="w-24 text-xs uppercase tracking-wider text-slate-400">{stageLabel(k)}</div>
                <div className="flex-1 h-6 rounded bg-[color:var(--bg-elev)] overflow-hidden">
                  <div className={`h-full ${stageColors[k] || "bg-slate-500"} transition-all`} style={{ width: `${pct}%` }} />
                </div>
                <div className="w-8 text-right text-xs font-mono">{v}</div>
              </div>
            );
          })}
        </div>
      </div>

      <LiveNowCard />

      <div className="tp-card p-6" data-testid="jobs-preview">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-semibold">Recent Jobs</div>
          <Link to="/app/jobs" className="text-xs text-indigo-400 hover:text-indigo-300">View all →</Link>
        </div>
        {jobsData === undefined ? null : jobs.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">No jobs yet. Create one to invite candidates.</div>
        ) : (
          <div className="space-y-2">
            {jobs.slice(0, 5).map((j) => (
              <Link key={j.id} to={`/app/jobs/${j.id}`} data-testid={`job-row-${j.id}`} className="flex items-center justify-between p-3 rounded-lg border border-transparent hover:border-[color:var(--bd-sub)] hover:bg-white/5 transition-colors">
                <div>
                  <div className="text-sm font-medium">{j.title}</div>
                  <div className="text-xs text-slate-500">{j.candidate_count} candidates</div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-slate-500" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
